import React, { useState, useEffect } from 'react';
import { getMyProfile, updateMyProfile } from '../../api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { User, Mail, Phone } from 'lucide-react';
import { getApiErrorMessage } from '../../utils/apiError';
import { toast } from 'sonner';

export default function PatientProfile() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({ email: '', consent_notifications: true });

  useEffect(() => {
    const load = async () => {
      try {
        const res = await getMyProfile();
        const data = res.data?.data ?? res.data;
        setProfile(data);
        setForm({
          email: data?.email || '',
          consent_notifications: data?.consent_notifications ?? true,
        });
      } catch (e) {
        toast.error(getApiErrorMessage(e, 'Error al cargar perfil'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await updateMyProfile(form);
      const data = res.data?.data ?? res.data;
      setProfile(data);
      setEdit(false);
      toast.success('Perfil actualizado');
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Error al guardar'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="w-10 h-10 border-4 border-[#335C67] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!profile) {
    return <p className="text-[#5D5C61]">No se pudo cargar tu perfil.</p>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#2B2D42] font-['Manrope']">Mi cuenta</h1>
        <p className="text-[#5D5C61] mt-1">Datos de tu perfil (solo puedes editar correo y notificaciones).</p>
      </div>

      <Card className="border-slate-100">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <User className="w-5 h-5 text-[#335C67]" />
            Datos personales
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-2">
            <Label className="text-[#8D99AE] flex items-center gap-2">
              <User className="w-4 h-4" /> Nombre completo
            </Label>
            <p className="text-[#2B2D42] font-medium">{profile.full_name}</p>
          </div>
          <div className="grid gap-2">
            <Label className="text-[#8D99AE] flex items-center gap-2">
              <Phone className="w-4 h-4" /> WhatsApp
            </Label>
            <p className="text-[#2B2D42]">{profile.whatsapp}</p>
          </div>
          {edit ? (
            <>
              <div className="grid gap-2">
                <Label className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-[#8D99AE]" />
                  Correo electrónico (opcional)
                </Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  placeholder="correo@ejemplo.com"
                />
              </div>
              <div className="flex items-center justify-between py-2 px-4 bg-[#F4F1EA] rounded-lg">
                <div>
                  <Label className="font-medium">Notificaciones</Label>
                  <p className="text-sm text-[#5D5C61]">Recibir recordatorios de citas</p>
                </div>
                <Switch
                  checked={form.consent_notifications}
                  onCheckedChange={(checked) => setForm((f) => ({ ...f, consent_notifications: checked }))}
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={handleSave} disabled={saving} className="bg-[#335C67] hover:bg-[#244048] rounded-full">
                  {saving ? 'Guardando...' : 'Guardar'}
                </Button>
                <Button variant="outline" onClick={() => setEdit(false)} disabled={saving} className="rounded-full">
                  Cancelar
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="grid gap-2">
                <Label className="text-[#8D99AE] flex items-center gap-2">
                  <Mail className="w-4 h-4" /> Correo
                </Label>
                <p className="text-[#2B2D42]">{profile.email || '—'}</p>
              </div>
              <div className="flex items-center justify-between py-2 px-4 bg-[#F4F1EA] rounded-lg">
                <div>
                  <Label className="font-medium">Notificaciones</Label>
                  <p className="text-sm text-[#5D5C61]">Recibir recordatorios de citas</p>
                </div>
                <Switch checked={profile.consent_notifications} disabled />
              </div>
              <Button onClick={() => setEdit(true)} variant="outline" className="rounded-full">
                Editar correo y notificaciones
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
